import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Grid,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  Badge,
  VStack,
  HStack,
  Avatar,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useUserPosts } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user: currentUser, isAuthenticated, logout } = useAuth();

  // Redirect if not authenticated - with delay to allow auth context to update
  useEffect(() => {
    const checkAuth = () => {
      // Check localStorage directly as backup
      const accessToken = localStorage.getItem('accessToken');
      const userStr = localStorage.getItem('user');
      
      if (!isAuthenticated && !accessToken && !userStr) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to view your profile',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        navigate('/login');
      }
    };

    // Add a small delay to allow auth context to update after login
    const timer = setTimeout(checkAuth, 100);
    return () => clearTimeout(timer);
  }, [isAuthenticated, navigate, toast]);

  // Fetch user posts
  const { 
    data: userPostsData, 
    isLoading: postsLoading, 
    error: postsError 
  } = useUserPosts(currentUser?.userId || '');

  const userPosts = userPostsData?.items || [];
  const totalPosts = userPostsData?.totalCount || 0;

  const handleLogout = () => {
    logout();
    toast({
      title: 'Logged out successfully',
      status: 'success',
      duration: 2000,
    });
    navigate('/');
  };

  const handleEditProfile = () => {
    toast({
      title: 'Edit Profile',
      description: 'Profile editing feature coming soon!',
      status: 'info',
      duration: 3000,
    });
  };

  const handleCreatePost = () => {
    navigate('/create-post');
  };

  const handleViewPost = (postId: string) => {
    navigate(`/post/${postId}`);
  };

  if (!isAuthenticated || !currentUser) {
    return null; // Will redirect in useEffect
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Profile Header */}
        <Box bg="white" p={8} borderRadius="lg" shadow="sm" border="1px" borderColor="gray.200">
          <Grid templateColumns={{ base: '1fr', md: 'auto 1fr auto' }} gap={6} alignItems="center">
            <Avatar 
              size="xl" 
              src={currentUser.profilePicture} 
              name={`${currentUser.firstName} ${currentUser.lastName}`}
            />
            
            <VStack align="start" spacing={2}>
              <Heading size="lg">
                {currentUser.firstName} {currentUser.lastName}
              </Heading>
              <Text color="gray.600">{currentUser.email}</Text>
              <HStack>
                <Badge colorScheme={currentUser.isVerified ? 'green' : 'yellow'}>
                  {currentUser.isVerified ? 'Verified' : 'Unverified'}
                </Badge>
                <Text fontSize="sm" color="gray.500">
                  Member since {new Date(currentUser.joinDate).toLocaleDateString()}
                </Text>
              </HStack>
            </VStack>

            <VStack spacing={2}>
              <Button colorScheme="brand.green" onClick={handleEditProfile}>
                Edit Profile
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                Logout
              </Button>
            </VStack>
          </Grid>
        </Box>

        {/* Stats */}
        <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={6}>
          <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px" borderColor="gray.200">
            <Stat>
              <StatLabel>Total Listings</StatLabel>
              <StatNumber>{totalPosts}</StatNumber>
              <StatHelpText>Active and sold items</StatHelpText>
            </Stat>
          </Box>
          
          <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px" borderColor="gray.200">
            <Stat>
              <StatLabel>Total Sales</StatLabel>
              <StatNumber>{currentUser.totalSales || 0}</StatNumber>
              <StatHelpText>Items sold</StatHelpText>
            </Stat>
          </Box>
          
          <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px" borderColor="gray.200">
            <Stat>
              <StatLabel>Total Purchases</StatLabel>
              <StatNumber>{currentUser.totalPurchases || 0}</StatNumber>
              <StatHelpText>Items bought</StatHelpText>
            </Stat>
          </Box>
          
          <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px" borderColor="gray.200">
            <Stat>
              <StatLabel>Rating</StatLabel>
              <StatNumber>{currentUser.rating || 0}/5</StatNumber>
              <StatHelpText>User rating</StatHelpText>
            </Stat>
          </Box>
        </Grid>

        {/* Recent Listings */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px" borderColor="gray.200">
          <HStack justify="space-between" mb={6}>
            <Heading size="md">Your Listings</Heading>
            <Button colorScheme="brand.green" onClick={handleCreatePost}>
              Create New Listing
            </Button>
          </HStack>

          <Divider mb={6} />

          {postsLoading ? (
            <Box textAlign="center" py={8}>
              <Spinner size="lg" color="brand.green.medium" />
              <Text mt={2}>Loading your listings...</Text>
            </Box>
          ) : postsError ? (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <VStack align="start" spacing={2}>
                <Text fontWeight="bold">Failed to load your listings</Text>
                <Text fontSize="sm">
                  {postsError instanceof Error ? postsError.message : 'An unexpected error occurred'}
                </Text>
              </VStack>
            </Alert>
          ) : userPosts.length > 0 ? (
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
              {userPosts.slice(0, 6).map((post) => (
                <Box 
                  key={post.postId} 
                  p={4} 
                  border="1px" 
                  borderColor="gray.200" 
                  borderRadius="md" 
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                  onClick={() => handleViewPost(post.postId)}
                >
                  <VStack align="start" spacing={3}>
                    {post.images && post.images[0] && (
                      <Box
                        w="full"
                        h="120px"
                        bg="gray.100"
                        borderRadius="md"
                        backgroundImage={`url(${post.images[0]})`}
                        backgroundSize="cover"
                        backgroundPosition="center"
                      />
                    )}
                    <VStack align="start" spacing={1} w="full">
                      <Text fontWeight="bold" fontSize="md" noOfLines={2}>
                        {post.title}
                      </Text>
                      <Text color="brand.green.medium" fontWeight="semibold" fontSize="lg">
                        ${post.price}
                      </Text>
                      <HStack>
                        <Badge 
                          colorScheme={post.status === 'active' ? 'green' : post.status === 'sold' ? 'blue' : 'gray'}
                          variant="subtle"
                        >
                          {post.status}
                        </Badge>
                        <Badge variant="outline">
                          {post.condition}
                        </Badge>
                      </HStack>
                      <Text fontSize="xs" color="gray.500">
                        {post.viewCount || 0} views • Created {new Date(post.createdAt).toLocaleDateString()}
                      </Text>
                    </VStack>
                  </VStack>
                </Box>
              ))}
            </Grid>
          ) : (
            <Box textAlign="center" py={12}>
              <Text fontSize="lg" color="gray.500" mb={4}>
                You haven't created any listings yet
              </Text>
              <Text color="gray.400" mb={6}>
                Start selling by creating your first listing
              </Text>
              <Button colorScheme="brand.green" onClick={handleCreatePost}>
                Create Your First Listing
              </Button>
            </Box>
          )}

          {userPosts.length > 6 && (
            <Box textAlign="center" mt={6}>
              <Button variant="outline" onClick={() => navigate('/my-posts')}>
                View All Listings ({totalPosts})
              </Button>
            </Box>
          )}
        </Box>
      </VStack>
    </Container>
  );
};

export default ProfilePage; 